/**
 * Expandable panel shown inside an assistant message: SpecTree + vision
 * critique (if present) + cut-list/DXF panel, gated on the message having
 * a structured artifact.spec.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { Message } from '@shared/types';
import { TreeNode } from '@shared/Tree';
import { SpecTree } from './SpecTree';
import { CritiqueDisplay, type CritiqueData } from './CritiqueDisplay';
import { CutListPanel } from './CutListPanel';
import { useCheckpoints } from '@/services/checkpointService';
import { cn } from '@/lib/utils';

interface Props {
  message: TreeNode<Message>;
}

export function SpecDetails({ message }: Props) {
  const { t } = useTranslation('common');
  const { id: conversationId } = useParams();
  const { data: checkpoints = [] } = useCheckpoints(conversationId);
  const [open, setOpen] = useState(false);

  const spec = message.content.artifact?.spec;

  // A checkpoint is associated via userMessageId = the user message that
  // triggered this assistant turn. Pick the most recent matching one.
  const linkedCheckpoint = useMemo(() => {
    const triggerUserMessageId = message.parent_message_id ?? undefined;
    if (!triggerUserMessageId) return null;
    const matches = checkpoints.filter(
      (c) => c.userMessageId === triggerUserMessageId,
    );
    if (matches.length === 0) return null;
    // Most recent (already sorted asc by createdAt).
    return matches[matches.length - 1];
  }, [checkpoints, message.parent_message_id]);

  const critique = (linkedCheckpoint as unknown as { critique?: CritiqueData })
    ?.critique;

  if (!spec) return null;

  return (
    <div className="rounded-md border bg-card/60">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'w-full flex items-center gap-2 px-3 py-2 text-xs font-semibold',
          'hover:bg-muted/50 transition',
        )}
      >
        {open ? (
          <ChevronDown className="w-3 h-3" />
        ) : (
          <ChevronRight className="w-3 h-3" />
        )}
        {t('spec.specPrefix')}{spec.parts.length}{t('spec.specPartsSuffix')}{spec.joints.length}{t('spec.specJointsSuffix')}
        {critique?.issues && critique.issues.length > 0 && (
          <span className="ml-auto font-bold text-brand-indigo">
            {critique.issues.length} {t('spec.visualNotes')}
          </span>
        )}
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3">
          <SpecTree spec={spec} />
          {critique && <CritiqueDisplay critique={critique} />}
          <CutListPanel
            conversationId={conversationId}
            checkpointId={linkedCheckpoint?.id}
          />
        </div>
      )}
    </div>
  );
}
